import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    ChartBarIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    SignalIcon,
    Squares2X2Icon,
    UserGroupIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { extractApiError } from "@/lib/api-error";
import {
    buildBreakdownRows,
    collectPaginatedRows,
    countBy,
    createExportFileName,
    exportRide2FiveExcelReport,
    getAppliedFilterEntries,
} from "@/lib/export/excel";
import {
    UsersLiveMap,
    type UsersLiveMapViewport,
} from "../components/UsersLiveMap";
import {
    getUsersIntelligenceDirectory,
    getUsersIntelligenceMap,
    getUsersIntelligenceSummary,
} from "../api/usersIntelligenceApi";
import type {
    UsersIntelligenceAppState,
    UsersIntelligenceDirectoryQueryParams,
    UsersIntelligenceMapQueryParams,
    UsersIntelligenceOnlineStatus,
    UsersIntelligenceRole,
    UsersIntelligenceStatus,
    UsersIntelligenceSummary,
} from "../types";

const ROLE_OPTIONS: Array<{ value: UsersIntelligenceRole | ""; label: string }> = [
    { value: "", label: "All Roles" },
    { value: "RIDER", label: "Riders" },
    { value: "DRIVER", label: "Drivers" },
    { value: "ADMIN", label: "Admins" },
    { value: "SUPER_ADMIN", label: "Super Admins" },
];

const STATUS_OPTIONS: Array<{ value: UsersIntelligenceStatus | ""; label: string }> = [
    { value: "", label: "All Statuses" },
    { value: "ACTIVE", label: "Active" },
    { value: "INACTIVE", label: "Inactive" },
    { value: "SUSPENDED", label: "Suspended" },
    { value: "BANNED", label: "Banned" },
    { value: "DELETED", label: "Deleted" },
];

const ONLINE_STATUS_OPTIONS: Array<{
    value: UsersIntelligenceOnlineStatus | "";
    label: string;
}> = [
    { value: "", label: "Any Presence" },
    { value: "ONLINE", label: "Online" },
    { value: "OFFLINE", label: "Offline" },
];

const APP_STATE_OPTIONS: Array<{ value: UsersIntelligenceAppState | ""; label: string }> = [
    { value: "", label: "Any App State" },
    { value: "FOREGROUND", label: "Foreground" },
    { value: "BACKGROUND", label: "Background" },
    { value: "INACTIVE", label: "Inactive" },
    { value: "UNKNOWN", label: "Unknown" },
];

const SUMMARY_WINDOWS = [5, 15, 30, 60];
const MAP_FRESHNESS_WINDOWS = [5, 15, 30, 60];
const POINTS_ZOOM_THRESHOLD = 13;

const formatNumber = (value: number | null | undefined) => {
    if (value == null) return "—";
    return new Intl.NumberFormat("en-NG").format(value);
};

const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return "—";

    const timestamp = new Date(dateString).getTime();

    if (Number.isNaN(timestamp)) {
        return "—";
    }

    const elapsedMs = Math.max(Date.now() - timestamp, 0);
    const elapsedMinutes = Math.floor(elapsedMs / 60_000);

    if (elapsedMinutes < 1) return "Just now";
    if (elapsedMinutes < 60) {
        return `${elapsedMinutes} min${elapsedMinutes === 1 ? "" : "s"} ago`;
    }

    const elapsedHours = Math.floor(elapsedMinutes / 60);

    if (elapsedHours < 24) {
        return `${elapsedHours} hour${elapsedHours === 1 ? "" : "s"} ago`;
    }

    const elapsedDays = Math.floor(elapsedHours / 24);

    if (elapsedDays < 7) {
        return `${elapsedDays} day${elapsedDays === 1 ? "" : "s"} ago`;
    }

    return formatDate(dateString);
};

const formatEnumLabel = (value: string | null) => {
    if (!value) return "—";
    return value
        .toLowerCase()
        .split("_")
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(" ");
};

const formatPercent = (value: number, total: number) => {
    if (!total) return "0.0%";
    return `${((value / total) * 100).toFixed(1)}%`;
};

const getStatusTone = (status: string) => {
    const tones: Record<string, string> = {
        ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        INACTIVE: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        SUSPENDED: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        BANNED: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
        DELETED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    };

    return tones[status] ?? "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
};

const getRoleTone = (role: string) => {
    const tones: Record<string, string> = {
        RIDER: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
        DRIVER: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
        ADMIN: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
        SUPER_ADMIN: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    };

    return tones[role] ?? "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
};

const getPresenceTone = (onlineStatus: string | null) =>
    onlineStatus === "ONLINE"
        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
        : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";

const getDetailPath = ({ userId, role }: { userId: string; role: string }) => {
    if (role === "RIDER") return `/riders/${userId}`;
    if (role === "DRIVER") return `/drivers/${userId}`;

    return null;
};

const StatCard = ({
    title,
    value,
    helper,
    icon: Icon,
    tone,
}: {
    title: string;
    value: string;
    helper: string;
    icon: React.ElementType;
    tone: string;
}) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-start justify-between gap-4">
            <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{title}</p>
                <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-slate-100">
                    {value}
                </p>
                <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">{helper}</p>
            </div>
            <div className={`rounded-2xl p-3 ${tone}`}>
                <Icon className="h-5 w-5" />
            </div>
        </div>
    </div>
);

const SectionCard = ({
    title,
    subtitle,
    action,
    children,
    className = "",
}: {
    title: string;
    subtitle?: string;
    action?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
}) => (
    <section
        className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 ${className}`.trim()}
    >
        <div className="flex flex-col gap-4 border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50 md:flex-row md:items-center md:justify-between">
            <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {title}
                </h2>
                {subtitle ? (
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        {subtitle}
                    </p>
                ) : null}
            </div>
            {action}
        </div>
        {children}
    </section>
);

const VerificationBadge = ({
    active,
    label,
}: {
    active: boolean;
    label: string;
}) => (
    <span
        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
            active
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
        }`}
    >
        {label}
    </span>
);

const PaginationBar = ({
    page,
    totalPages,
    totalElements,
    onPageChange,
}: {
    page: number;
    totalPages: number;
    totalElements: number;
    onPageChange: (page: number) => void;
}) => {
    if (totalPages <= 1) return null;

    return (
        <div className="flex flex-col gap-3 border-t border-slate-200 px-6 py-4 text-sm dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-slate-500 dark:text-slate-400">
                Page {page + 1} of {totalPages} · {formatNumber(totalElements)} total
            </p>
            <div className="flex gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 0}
                >
                    Prev
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages - 1}
                >
                    Next
                </Button>
            </div>
        </div>
    );
};

const SummaryPanels = ({ summary }: { summary: UsersIntelligenceSummary }) => (
    <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
                title="Total Accounts"
                value={formatNumber(summary.totals.totalAccounts)}
                helper={`${formatNumber(summary.totals.riderAccounts)} riders · ${formatNumber(summary.totals.driverAccounts)} drivers`}
                icon={UsersIcon}
                tone="bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
            />
            <StatCard
                title="Active Accounts"
                value={formatNumber(summary.totals.activeAccounts)}
                helper={`${formatNumber(summary.totals.inactiveAccounts)} inactive · ${formatNumber(summary.totals.deletedAccounts)} deleted`}
                icon={CheckCircleIcon}
                tone="bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400"
            />
            <StatCard
                title="Live Users"
                value={formatNumber(summary.presence.realtimeOnlineUsers)}
                helper={`${formatNumber(summary.presence.onlineRiders)} riders · ${formatNumber(summary.presence.onlineDrivers)} drivers online`}
                icon={SignalIcon}
                tone="bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400"
            />
            <StatCard
                title="Related Accounts"
                value={formatNumber(summary.relations.accountsWithAnyRelation)}
                helper={`${formatNumber(summary.relations.multiAccountGroups)} multi-account groups inferred`}
                icon={UserGroupIcon}
                tone="bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-400"
            />
        </div>

        <div className="grid gap-6 xl:grid-cols-3">
            <SectionCard
                title="Verification Coverage"
                subtitle="Account-level verification counts across the platform."
            >
                <div className="grid gap-4 p-6 sm:grid-cols-2">
                    {[
                        ["Email Verified", summary.verification.emailVerifiedAccounts],
                        ["Phone Verified", summary.verification.phoneVerifiedAccounts],
                        ["NIN Verified", summary.verification.ninVerifiedAccounts],
                        ["Liveness Checked", summary.verification.livenessCheckedAccounts],
                        ["ID Verified", summary.verification.idVerifiedAccounts],
                        ["2FA Enabled", summary.verification.twoFactorEnabledAccounts],
                    ].map(([label, value]) => (
                        <div
                            key={label}
                            className="rounded-xl border border-slate-200 p-4 dark:border-slate-700"
                        >
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                {label}
                            </p>
                            <p className="mt-2 text-xl font-bold text-slate-900 dark:text-slate-100">
                                {formatNumber(value as number)}
                            </p>
                        </div>
                    ))}
                </div>
            </SectionCard>

            <SectionCard
                title="Identity Relations"
                subtitle="Inference-based grouping across shared email, phone, and NIN."
            >
                <div className="grid gap-4 p-6 sm:grid-cols-2">
                    {[
                        ["Shared Email", summary.relations.accountsWithSharedEmail],
                        ["Shared Phone", summary.relations.accountsWithSharedPhone],
                        ["Any Relation", summary.relations.accountsWithAnyRelation],
                        ["Multi-role Accounts", summary.relations.multiRoleRelatedAccounts],
                        ["Identity Groups", summary.relations.estimatedIdentityGroups],
                        ["Accounts In Groups", summary.relations.accountsInMultiAccountGroups],
                    ].map(([label, value]) => (
                        <div
                            key={label}
                            className="rounded-xl border border-slate-200 p-4 dark:border-slate-700"
                        >
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                {label}
                            </p>
                            <p className="mt-2 text-xl font-bold text-slate-900 dark:text-slate-100">
                                {formatNumber(value as number)}
                            </p>
                        </div>
                    ))}
                </div>
            </SectionCard>

            <SectionCard
                title="Realtime Operations"
                subtitle="Freshness-aware presence counts from the unified live presence store."
            >
                <div className="grid gap-4 p-6 sm:grid-cols-2">
                    {[
                        ["Online Users", summary.presence.onlineUsers],
                        ["Foreground Users", summary.presence.foregroundUsers],
                        ["Live Locations", summary.presence.usersWithLiveLocation],
                        ["Seen Last 5m", summary.presence.seenLast5Minutes],
                        ["Seen Last 15m", summary.presence.seenLast15Minutes],
                        ["New 7 Days", summary.registrations.last7Days],
                    ].map(([label, value]) => (
                        <div
                            key={label}
                            className="rounded-xl border border-slate-200 p-4 dark:border-slate-700"
                        >
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                {label}
                            </p>
                            <p className="mt-2 text-xl font-bold text-slate-900 dark:text-slate-100">
                                {formatNumber(value as number)}
                            </p>
                        </div>
                    ))}
                </div>
            </SectionCard>
        </div>
    </div>
);

export const UsersRoute = () => {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canViewMap = hasPermission(SECTION_PERMISSIONS.USERS_MAP[0]);
    const canExportUsers = hasPermission(SECTION_PERMISSIONS.USERS_EXPORT[0]);
    const [showFilters, setShowFilters] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState("");
    const [summaryWindowMinutes, setSummaryWindowMinutes] = useState(15);
    const [directoryFilters, setDirectoryFilters] =
        useState<UsersIntelligenceDirectoryQueryParams>({
            page: 0,
            size: 10,
            role: "",
            status: "",
            search: "",
            emailVerified: "",
            phoneVerified: "",
            ninVerified: "",
            onlineStatus: "",
            appState: "",
            hasRelatedAccounts: "",
            createdFrom: "",
            createdTo: "",
        });
    const [mapFilters, setMapFilters] = useState<UsersIntelligenceMapQueryParams>({
        role: "",
        onlineStatus: "ONLINE",
        lastSeenWithinMinutes: 15,
        includeAdmins: false,
    });
    const [mapViewport, setMapViewport] = useState<UsersLiveMapViewport | null>(null);

    const summaryQuery = useQuery({
        queryKey: ["users-intelligence-summary", summaryWindowMinutes],
        queryFn: () => getUsersIntelligenceSummary(summaryWindowMinutes),
        refetchInterval: 30_000,
    });

    const directoryQuery = useQuery({
        queryKey: ["users-intelligence-directory", directoryFilters],
        queryFn: () => getUsersIntelligenceDirectory(directoryFilters),
    });

    const mapMode =
        mapViewport && mapViewport.zoom >= POINTS_ZOOM_THRESHOLD
            ? "POINTS"
            : "CLUSTERS";
    const effectiveMapFilters: UsersIntelligenceMapQueryParams = {
        ...mapFilters,
        mode: mapMode,
        minLat: mapViewport?.minLat,
        minLng: mapViewport?.minLng,
        maxLat: mapViewport?.maxLat,
        maxLng: mapViewport?.maxLng,
        limit: mapMode === "POINTS" ? 500 : 250,
        gridSize: mapMode === "CLUSTERS" ? 0.02 : undefined,
    };

    const mapQuery = useQuery({
        queryKey: ["users-intelligence-map", effectiveMapFilters],
        queryFn: () => getUsersIntelligenceMap(effectiveMapFilters),
        enabled: canViewMap && Boolean(mapViewport),
        placeholderData: keepPreviousData,
        refetchInterval: 30_000,
    });

    const summary = summaryQuery.data?.data;
    const directory = directoryQuery.data?.data;
    const mapData = mapQuery.data?.data;
    const mapErrorMessage = mapQuery.error
        ? "Failed to load live map data for the visible area."
        : null;

    const handleDirectoryFilterChange = (
        key: keyof UsersIntelligenceDirectoryQueryParams,
        value: string | number | boolean
    ) => {
        setDirectoryFilters((prev) => ({
            ...prev,
            [key]: value,
            page: key === "page" ? Number(value) : 0,
        }));
    };

    const handleMapViewportChange = (nextViewport: UsersLiveMapViewport) => {
        setMapViewport((currentViewport) => {
            if (
                currentViewport &&
                currentViewport.zoom === nextViewport.zoom &&
                Math.abs(currentViewport.minLat - nextViewport.minLat) < 0.0001 &&
                Math.abs(currentViewport.minLng - nextViewport.minLng) < 0.0001 &&
                Math.abs(currentViewport.maxLat - nextViewport.maxLat) < 0.0001 &&
                Math.abs(currentViewport.maxLng - nextViewport.maxLng) < 0.0001
            ) {
                return currentViewport;
            }

            return nextViewport;
        });
    };

    const handleMapUserSelect = (userId: string, role: string) => {
        const detailPath = getDetailPath({ userId, role });

        if (detailPath) {
            navigate(detailPath);
        }
    };

    const refreshAll = async () => {
        await Promise.all(
            [summaryQuery.refetch(), directoryQuery.refetch(), canViewMap ? mapQuery.refetch() : null].filter(
                Boolean
            )
        );
    };

    const handleExport = async () => {
        try {
            setIsExporting(true);
            setExportError("");

            const rows = await collectPaginatedRows({
                fetchPage: (page) =>
                    getUsersIntelligenceDirectory({
                        ...directoryFilters,
                        page,
                        size: 100,
                    }),
                getRows: (response) => response.data.users,
                getTotalPages: (response) => response.data.totalPages,
            });

            const roleCounts = countBy(rows, (row) => row.role);
            const statusCounts = countBy(rows, (row) => row.status);
            const presenceCounts = countBy(
                rows,
                (row) => row.presence?.onlineStatus ?? "NO_PRESENCE"
            );
            const filteredOnlineUsers = rows.filter(
                (row) => row.presence?.onlineStatus === "ONLINE"
            ).length;
            const relatedAccounts = rows.filter(
                (row) => row.relations.groupSize > 1
            ).length;
            const emailVerifiedCount = rows.filter((row) => row.emailVerified).length;
            const phoneVerifiedCount = rows.filter((row) => row.phoneVerified).length;
            const ninVerifiedCount = rows.filter((row) => row.ninVerified).length;
            const liveLocationCount = rows.filter(
                (row) =>
                    row.presence?.latitude != null && row.presence?.longitude != null
            ).length;

            await exportRide2FiveExcelReport({
                title: "Users Intelligence Export",
                subtitle:
                    "Consolidated riders, drivers, and admin accounts for the current directory filters.",
                fileName: createExportFileName("users_directory_export"),
                filters: directoryFilters,
                rows,
                metrics: [
                    {
                        label: "Filtered Accounts",
                        value: rows.length,
                    },
                    {
                        label: "Online In Filtered Set",
                        value: filteredOnlineUsers,
                        note: formatPercent(filteredOnlineUsers, rows.length),
                    },
                    {
                        label: "Related Accounts",
                        value: relatedAccounts,
                        note: formatPercent(relatedAccounts, rows.length),
                    },
                    {
                        label: "Live Location Coverage",
                        value: liveLocationCount,
                        note: formatPercent(liveLocationCount, rows.length),
                    },
                ],
                sections: [
                    ...(summary
                        ? [
                              {
                                  title: "Current Platform Snapshot",
                                  rows: [
                                      {
                                          label: "Total Accounts",
                                          value: summary.totals.totalAccounts,
                                      },
                                      {
                                          label: "Live Users",
                                          value: summary.presence.realtimeOnlineUsers,
                                          note: `Realtime window: ${summary.presence.realtimeWindowMinutes} minutes`,
                                      },
                                      {
                                          label: "Related Accounts",
                                          value: summary.relations.accountsWithAnyRelation,
                                      },
                                      {
                                          label: "New Registrations (30d)",
                                          value: summary.registrations.last30Days,
                                      },
                                  ],
                              },
                          ]
                        : []),
                    {
                        title: "Role Breakdown",
                        rows: buildBreakdownRows(roleCounts),
                    },
                    {
                        title: "Status Breakdown",
                        rows: buildBreakdownRows(statusCounts),
                    },
                    {
                        title: "Verification Coverage",
                        rows: [
                            {
                                label: "Email Verified",
                                value: emailVerifiedCount,
                                note: formatPercent(emailVerifiedCount, rows.length),
                            },
                            {
                                label: "Phone Verified",
                                value: phoneVerifiedCount,
                                note: formatPercent(phoneVerifiedCount, rows.length),
                            },
                            {
                                label: "NIN Verified",
                                value: ninVerifiedCount,
                                note: formatPercent(ninVerifiedCount, rows.length),
                            },
                        ],
                    },
                    {
                        title: "Presence Breakdown",
                        rows: buildBreakdownRows(presenceCounts),
                    },
                    {
                        title: "Applied Filters Summary",
                        rows:
                            getAppliedFilterEntries(directoryFilters).map((entry) => ({
                                label: entry.label,
                                value: entry.value,
                            })) || [],
                    },
                ],
                columns: [
                    {
                        header: "User ID",
                        width: 38,
                        value: (row) => row.userId,
                    },
                    {
                        header: "Full Name",
                        width: 24,
                        value: (row) => `${row.firstName} ${row.lastName}`,
                    },
                    {
                        header: "Role",
                        width: 16,
                        value: (row) => formatEnumLabel(row.role),
                    },
                    {
                        header: "Status",
                        width: 16,
                        value: (row) => formatEnumLabel(row.status),
                    },
                    {
                        header: "Email",
                        width: 28,
                        value: (row) => row.email,
                    },
                    {
                        header: "Phone",
                        width: 18,
                        value: (row) => row.phoneNumber,
                    },
                    {
                        header: "Email Verified",
                        width: 14,
                        alignment: "center",
                        value: (row) => (row.emailVerified ? "Yes" : "No"),
                    },
                    {
                        header: "Phone Verified",
                        width: 14,
                        alignment: "center",
                        value: (row) => (row.phoneVerified ? "Yes" : "No"),
                    },
                    {
                        header: "NIN Verified",
                        width: 14,
                        alignment: "center",
                        value: (row) => (row.ninVerified ? "Yes" : "No"),
                    },
                    {
                        header: "Online Status",
                        width: 16,
                        value: (row) =>
                            formatEnumLabel(row.presence?.onlineStatus ?? "NO_PRESENCE"),
                    },
                    {
                        header: "App State",
                        width: 16,
                        value: (row) =>
                            formatEnumLabel(row.presence?.appState ?? null),
                    },
                    {
                        header: "Last Seen",
                        width: 22,
                        value: (row) =>
                            row.presence?.lastSeenAt
                                ? formatDate(row.presence.lastSeenAt)
                                : "—",
                    },
                    {
                        header: "Related Group Size",
                        width: 16,
                        alignment: "right",
                        value: (row) => row.relations.groupSize,
                    },
                    {
                        header: "Identity Type",
                        width: 18,
                        value: (row) => formatEnumLabel(row.relations.identityType),
                    },
                    {
                        header: "Identity Roles",
                        width: 22,
                        value: (row) => row.relations.identityRoles.join(", "),
                    },
                    {
                        header: "Created At",
                        width: 22,
                        value: (row) => formatDate(row.createdAt),
                    },
                    {
                        header: "Last Login",
                        width: 22,
                        value: (row) =>
                            row.lastLoginAt ? formatDate(row.lastLoginAt) : "Never",
                    },
                ],
                dataSheetName: "Users Directory",
            });
        } catch (error) {
            setExportError(extractApiError(error));
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="space-y-6">
            <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                    <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-slate-50">
                        Users
                    </h1>
                    <p className="mt-1 max-w-3xl text-sm text-slate-500 dark:text-slate-300">
                        Consolidated account visibility across riders, drivers, and admin users,
                        powered by identity inference and live presence-aware operations data.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <ClockIcon className="h-4 w-4 text-slate-400" />
                        <select
                            value={summaryWindowMinutes}
                            onChange={(event) =>
                                setSummaryWindowMinutes(Number(event.target.value))
                            }
                            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                        >
                            {SUMMARY_WINDOWS.map((windowMinutes) => (
                                <option key={windowMinutes} value={windowMinutes}>
                                    Realtime window: {windowMinutes}m
                                </option>
                            ))}
                        </select>
                    </div>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={refreshAll}
                        disabled={
                            summaryQuery.isFetching ||
                            directoryQuery.isFetching ||
                            mapQuery.isFetching
                        }
                    >
                        <ArrowPathIcon
                            className={`mr-1.5 h-4 w-4 ${
                                summaryQuery.isFetching ||
                                directoryQuery.isFetching ||
                                mapQuery.isFetching
                                    ? "animate-spin"
                                    : ""
                            }`}
                        />
                        Refresh
                    </Button>
                    {canExportUsers ? (
                        <Button
                            size="sm"
                            onClick={handleExport}
                            disabled={isExporting}
                        >
                            <ArrowDownTrayIcon className="mr-1.5 h-4 w-4" />
                            {isExporting ? "Exporting..." : "Export Excel"}
                        </Button>
                    ) : null}
                </div>
            </section>

            {exportError ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
                    {exportError}
                </div>
            ) : null}

            {summaryQuery.isLoading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                </div>
            ) : summaryQuery.error || !summary ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                    <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                    <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">
                        Failed to load users intelligence summary
                    </h3>
                </div>
            ) : (
                <SummaryPanels summary={summary} />
            )}

            <SectionCard
                title="Directory"
                subtitle="Consolidated users table with identity relation and presence-aware filtering."
                action={
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowFilters((prev) => !prev)}
                    >
                        <FunnelIcon className="mr-1.5 h-4 w-4" />
                        Filters
                    </Button>
                }
            >
                <div className="space-y-4 p-6">
                    <div className="relative max-w-lg">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search by name, email, or phone..."
                            value={directoryFilters.search || ""}
                            onChange={(event) =>
                                handleDirectoryFilterChange("search", event.target.value)
                            }
                            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                        />
                    </div>

                    {showFilters ? (
                        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/30 md:grid-cols-2 xl:grid-cols-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Role
                                </label>
                                <select
                                    value={directoryFilters.role || ""}
                                    onChange={(event) =>
                                        handleDirectoryFilterChange("role", event.target.value)
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                >
                                    {ROLE_OPTIONS.map((option) => (
                                        <option key={option.label} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Status
                                </label>
                                <select
                                    value={directoryFilters.status || ""}
                                    onChange={(event) =>
                                        handleDirectoryFilterChange("status", event.target.value)
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                >
                                    {STATUS_OPTIONS.map((option) => (
                                        <option key={option.label} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Presence
                                </label>
                                <select
                                    value={directoryFilters.onlineStatus || ""}
                                    onChange={(event) =>
                                        handleDirectoryFilterChange(
                                            "onlineStatus",
                                            event.target.value
                                        )
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                >
                                    {ONLINE_STATUS_OPTIONS.map((option) => (
                                        <option key={option.label} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    App State
                                </label>
                                <select
                                    value={directoryFilters.appState || ""}
                                    onChange={(event) =>
                                        handleDirectoryFilterChange("appState", event.target.value)
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                >
                                    {APP_STATE_OPTIONS.map((option) => (
                                        <option key={option.label} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Email Verified
                                </label>
                                <select
                                    value={
                                        directoryFilters.emailVerified === ""
                                            ? ""
                                            : directoryFilters.emailVerified
                                              ? "true"
                                              : "false"
                                    }
                                    onChange={(event) =>
                                        handleDirectoryFilterChange(
                                            "emailVerified",
                                            event.target.value === ""
                                                ? ""
                                                : event.target.value === "true"
                                        )
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                >
                                    <option value="">All</option>
                                    <option value="true">Verified</option>
                                    <option value="false">Not Verified</option>
                                </select>
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Phone Verified
                                </label>
                                <select
                                    value={
                                        directoryFilters.phoneVerified === ""
                                            ? ""
                                            : directoryFilters.phoneVerified
                                              ? "true"
                                              : "false"
                                    }
                                    onChange={(event) =>
                                        handleDirectoryFilterChange(
                                            "phoneVerified",
                                            event.target.value === ""
                                                ? ""
                                                : event.target.value === "true"
                                        )
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                >
                                    <option value="">All</option>
                                    <option value="true">Verified</option>
                                    <option value="false">Not Verified</option>
                                </select>
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    NIN Verified
                                </label>
                                <select
                                    value={
                                        directoryFilters.ninVerified === ""
                                            ? ""
                                            : directoryFilters.ninVerified
                                              ? "true"
                                              : "false"
                                    }
                                    onChange={(event) =>
                                        handleDirectoryFilterChange(
                                            "ninVerified",
                                            event.target.value === ""
                                                ? ""
                                                : event.target.value === "true"
                                        )
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                >
                                    <option value="">All</option>
                                    <option value="true">Verified</option>
                                    <option value="false">Not Verified</option>
                                </select>
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Related Accounts
                                </label>
                                <select
                                    value={
                                        directoryFilters.hasRelatedAccounts === ""
                                            ? ""
                                            : directoryFilters.hasRelatedAccounts
                                              ? "true"
                                              : "false"
                                    }
                                    onChange={(event) =>
                                        handleDirectoryFilterChange(
                                            "hasRelatedAccounts",
                                            event.target.value === ""
                                                ? ""
                                                : event.target.value === "true"
                                        )
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                >
                                    <option value="">All</option>
                                    <option value="true">Has Related Accounts</option>
                                    <option value="false">Standalone Only</option>
                                </select>
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Created From
                                </label>
                                <input
                                    type="datetime-local"
                                    value={directoryFilters.createdFrom || ""}
                                    onChange={(event) =>
                                        handleDirectoryFilterChange(
                                            "createdFrom",
                                            event.target.value
                                        )
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Created To
                                </label>
                                <input
                                    type="datetime-local"
                                    value={directoryFilters.createdTo || ""}
                                    onChange={(event) =>
                                        handleDirectoryFilterChange(
                                            "createdTo",
                                            event.target.value
                                        )
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                        </div>
                    ) : null}
                </div>

                {directoryQuery.isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                    </div>
                ) : directoryQuery.error || !directory ? (
                    <div className="px-6 pb-6">
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center dark:border-rose-800 dark:bg-rose-900/20">
                            <ExclamationTriangleIcon className="mx-auto h-8 w-8 text-rose-400" />
                            <p className="mt-3 text-sm font-medium text-rose-700 dark:text-rose-300">
                                Failed to load users directory.
                            </p>
                        </div>
                    </div>
                ) : !directory.users.length ? (
                    <div className="px-6 pb-6">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-12 text-center dark:border-slate-700 dark:bg-slate-800/30">
                            <UsersIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
                            <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">
                                No users matched the current filters.
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                                <thead className="bg-slate-50 dark:bg-slate-800/60">
                                    <tr>
                                        {[
                                            "User",
                                            "Role",
                                            "Status",
                                            "Verification",
                                            "Relations",
                                            "Presence",
                                            "Created",
                                        ].map((header) => (
                                            <th
                                                key={header}
                                                className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
                                            >
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                    {directory.users.map((user) => {
                                        const detailPath = getDetailPath(user);

                                        return (
                                            <tr
                                                key={user.userId}
                                                className="transition hover:bg-slate-50 dark:hover:bg-slate-800/40"
                                            >
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        {user.profilePicture ? (
                                                            <img
                                                                src={user.profilePicture}
                                                                alt={`${user.firstName} ${user.lastName}`}
                                                                className="h-10 w-10 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                                                                {user.firstName[0]}
                                                                {user.lastName[0]}
                                                            </div>
                                                        )}
                                                        <div>
                                                            {detailPath ? (
                                                                <button
                                                                    onClick={() => navigate(detailPath)}
                                                                    className="text-left text-sm font-semibold text-slate-900 transition hover:text-primary-600 dark:text-slate-100 dark:hover:text-primary-400"
                                                                >
                                                                    {user.firstName} {user.lastName}
                                                                </button>
                                                            ) : (
                                                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                                    {user.firstName} {user.lastName}
                                                                </p>
                                                            )}
                                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                                {user.email}
                                                            </p>
                                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                                {user.phoneNumber}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span
                                                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getRoleTone(
                                                            user.role
                                                        )}`}
                                                    >
                                                        {formatEnumLabel(user.role)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="space-y-2">
                                                        <span
                                                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getStatusTone(
                                                                user.status
                                                            )}`}
                                                        >
                                                            {formatEnumLabel(user.status)}
                                                        </span>
                                                        {user.lastLoginAt ? (
                                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                                Last login {formatRelativeTime(user.lastLoginAt)}
                                                            </p>
                                                        ) : null}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex max-w-xs flex-wrap gap-2">
                                                        <VerificationBadge
                                                            active={user.emailVerified}
                                                            label="Email"
                                                        />
                                                        <VerificationBadge
                                                            active={user.phoneVerified}
                                                            label="Phone"
                                                        />
                                                        <VerificationBadge
                                                            active={user.ninVerified}
                                                            label="NIN"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="space-y-2">
                                                        <span
                                                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                                                                user.relations.groupSize > 1
                                                                    ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                                                                    : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                                                            }`}
                                                        >
                                                            {user.relations.groupSize > 1
                                                                ? `${user.relations.groupSize} related accounts`
                                                                : "Standalone"}
                                                        </span>
                                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                                            {formatEnumLabel(
                                                                user.relations.identityType
                                                            )}{" "}
                                                            · {user.relations.identityRoles.join(" + ")}
                                                        </p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {user.presence ? (
                                                        <div className="space-y-2">
                                                            <span
                                                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getPresenceTone(
                                                                    user.presence.onlineStatus
                                                                )}`}
                                                            >
                                                                {formatEnumLabel(
                                                                    user.presence.onlineStatus
                                                                )}
                                                            </span>
                                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                                {formatEnumLabel(
                                                                    user.presence.appState
                                                                )}{" "}
                                                                ·{" "}
                                                                {formatRelativeTime(
                                                                    user.presence.lastSeenAt
                                                                )}
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-slate-400 dark:text-slate-500">
                                                            No presence row
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="text-sm text-slate-700 dark:text-slate-300">
                                                        {formatDate(user.createdAt)}
                                                    </p>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <PaginationBar
                            page={directory.page}
                            totalPages={directory.totalPages}
                            totalElements={directory.totalElements}
                            onPageChange={(page) =>
                                setDirectoryFilters((prev) => ({ ...prev, page }))
                            }
                        />
                    </>
                )}
            </SectionCard>

            {canViewMap ? (
                <SectionCard
                    title="Live Operations Map"
                    subtitle={`Viewport-aware OpenStreetMap view. The page auto-switches from clusters to raw points at zoom ${POINTS_ZOOM_THRESHOLD}+ for more efficient loading.`}
                    action={
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                Auto Mode: {mapMode === "POINTS" ? "Points" : "Clusters"}
                            </span>
                            <select
                                value={mapFilters.role || ""}
                                onChange={(event) =>
                                    setMapFilters((prev) => ({
                                        ...prev,
                                        role: event.target.value as UsersIntelligenceRole | "",
                                    }))
                                }
                                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                            >
                                {ROLE_OPTIONS.map((option) => (
                                    <option key={option.label} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={mapFilters.lastSeenWithinMinutes || 15}
                                onChange={(event) =>
                                    setMapFilters((prev) => ({
                                        ...prev,
                                        lastSeenWithinMinutes: Number(event.target.value),
                                    }))
                                }
                                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                            >
                                {MAP_FRESHNESS_WINDOWS.map((windowMinutes) => (
                                    <option key={windowMinutes} value={windowMinutes}>
                                        Seen within {windowMinutes}m
                                    </option>
                                ))}
                            </select>
                        </div>
                    }
                >
                    <div className="space-y-6 p-6">
                    <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700">
                        <UsersLiveMap
                            mode={mapMode}
                            points={mapData?.points ?? []}
                            clusters={mapData?.clusters ?? []}
                            isLoading={!mapViewport || (mapQuery.isLoading && !mapData)}
                            errorMessage={mapErrorMessage}
                            onViewportChange={handleMapViewportChange}
                            onUserSelect={handleMapUserSelect}
                        />
                    </div>

                    {mapData ? (
                        <>
                            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                                {[
                                    {
                                        label: "Matched Users",
                                        value: mapData.totals.totalMatched,
                                        Icon: Squares2X2Icon,
                                    },
                                    {
                                        label: "Riders",
                                        value: mapData.totals.riders,
                                        Icon: UserGroupIcon,
                                    },
                                    {
                                        label: "Drivers",
                                        value: mapData.totals.drivers,
                                        Icon: UsersIcon,
                                    },
                                    {
                                        label: "Admins",
                                        value: mapData.totals.admins,
                                        Icon: ChartBarIcon,
                                    },
                                    {
                                        label: "Foreground",
                                        value: mapData.totals.foregroundUsers,
                                        Icon: SignalIcon,
                                    },
                                ].map(({ label, value, Icon }) => (
                                    <div
                                        key={label}
                                        className="rounded-xl border border-slate-200 p-4 dark:border-slate-700"
                                    >
                                        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                                            <Icon className="h-4 w-4" />
                                            <span className="text-xs font-medium uppercase tracking-wide">
                                                {label}
                                            </span>
                                        </div>
                                        <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-slate-100">
                                            {formatNumber(value as number)}
                                        </p>
                                    </div>
                                ))}
                            </div>

                            <div className="grid gap-4 lg:grid-cols-2">
                                <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                        Viewport
                                    </h3>
                                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                        <div>
                                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                Min Latitude
                                            </p>
                                            <p className="mt-1 font-mono text-sm text-slate-900 dark:text-slate-100">
                                                {mapViewport?.minLat.toFixed(4) ?? "—"}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                Max Latitude
                                            </p>
                                            <p className="mt-1 font-mono text-sm text-slate-900 dark:text-slate-100">
                                                {mapViewport?.maxLat.toFixed(4) ?? "—"}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                Min Longitude
                                            </p>
                                            <p className="mt-1 font-mono text-sm text-slate-900 dark:text-slate-100">
                                                {mapViewport?.minLng.toFixed(4) ?? "—"}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                Max Longitude
                                            </p>
                                            <p className="mt-1 font-mono text-sm text-slate-900 dark:text-slate-100">
                                                {mapViewport?.maxLng.toFixed(4) ?? "—"}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                Zoom
                                            </p>
                                            <p className="mt-1 font-mono text-sm text-slate-900 dark:text-slate-100">
                                                {mapViewport?.zoom ?? "—"}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                Returned Mode
                                            </p>
                                            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                {formatEnumLabel(mapData.mode)}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                        Latest Activity
                                    </h3>
                                    <div className="mt-3 space-y-3">
                                        {(mapData.mode === "CLUSTERS"
                                            ? mapData.clusters
                                                  .slice(0, 5)
                                                  .map((cluster, index) => ({
                                                      key: `${cluster.latitude}-${cluster.longitude}-${index}`,
                                                      title: `${cluster.totalUsers} users`,
                                                      subtitle: `${cluster.riders} riders · ${cluster.drivers} drivers`,
                                                      timestamp: cluster.latestSeenAt,
                                                  }))
                                            : mapData.points.slice(0, 5).map((point) => ({
                                                  key: point.userId,
                                                  title: `${point.firstName} ${point.lastName}`,
                                                  subtitle: `${formatEnumLabel(point.role)} · ${formatEnumLabel(point.onlineStatus)}`,
                                                  timestamp: point.lastSeenAt,
                                              }))
                                        ).map((entry) => (
                                            <div
                                                key={entry.key}
                                                className="flex items-start justify-between gap-4 rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/40"
                                            >
                                                <div>
                                                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                                        {entry.title}
                                                    </p>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        {entry.subtitle}
                                                    </p>
                                                </div>
                                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                                    {formatRelativeTime(entry.timestamp)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : mapViewport && !mapQuery.isLoading && !mapQuery.error ? (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/30 dark:text-slate-400">
                            The map is ready. Move or zoom to request live presence data for the
                            visible area.
                        </div>
                    ) : null}
                    </div>
                </SectionCard>
            ) : null}

            {summary ? (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                    Summary generated {formatDate(summary.generatedAt)}.
                    {directory?.generatedAt
                        ? ` Directory generated ${formatDate(directory.generatedAt)}.`
                        : ""}
                    {mapData?.generatedAt
                        ? ` Map generated ${formatDate(mapData.generatedAt)}.`
                        : ""}
                </p>
            ) : null}
        </div>
    );
};
